/**
 * 그림판 카메라 — 끌어서 옮기고, 휠·핀치·단추로 키우고 줄이고, 고른 영역으로 맞춰 가는 셈.
 *
 * 그림(월드)은 제 크기 그대로 그려 두고, 카메라가 `translate(x, y) scale(k)` 하나로 보이는 자리를 정한다.
 * 화면 좌표 p 의 월드 좌표는 (p - (x, y)) / k 다. 셈만 여기 두고(시험한다), 이벤트는 그리는 쪽이 받는다.
 */

export interface Camera {
  x: number;
  y: number;
  k: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** 보이는 판에서 떠 있는 판(메뉴·도구 줄)이 가린 가장자리 — 맞출 때 이 안쪽에 놓는다. */
export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const ZOOM_MIN = 0.15;
export const ZOOM_MAX = 2.5;
/** 단추·키 한 번의 배율 걸음. */
export const ZOOM_STEP = 1.25;

export const clampZoom = (k: number): number => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, k));

/** 화면의 한 점(커서·핀치 가운데)을 붙든 채 배율을 바꾼다 — 그 점 아래의 그림이 제자리에 남는다. */
export function zoomAt(camera: Camera, factor: number, point: { x: number; y: number }): Camera {
  const k = clampZoom(camera.k * factor);
  const ratio = k / camera.k;
  return { k, x: point.x - (point.x - camera.x) * ratio, y: point.y - (point.y - camera.y) * ratio };
}

export function panBy(camera: Camera, dx: number, dy: number): Camera {
  return { ...camera, x: camera.x + dx, y: camera.y + dy };
}

/**
 * 월드의 한 사각형을 보이는 판(가린 가장자리를 뺀 안쪽)에 꽉 차게 맞춘다. `maxK` 보다 키우지 않는다 — 화면
 * 하나를 고른다고 썸네일이 판을 덮을 만큼 커지면 어디인지 잃는다.
 */
export function fitRect(rect: Rect, view: { width: number; height: number }, insets: Insets, options: { margin?: number; maxK?: number } = {}): Camera {
  const margin = options.margin ?? 24;
  const width = Math.max(1, view.width - insets.left - insets.right - margin * 2);
  const height = Math.max(1, view.height - insets.top - insets.bottom - margin * 2);
  const k = clampZoom(Math.min(width / Math.max(1, rect.width), height / Math.max(1, rect.height), options.maxK ?? 1));
  const cx = insets.left + margin + width / 2;
  const cy = insets.top + margin + height / 2;
  return { k, x: cx - (rect.x + rect.width / 2) * k, y: cy - (rect.y + rect.height / 2) * k };
}

/** 핀치 한 걸음 — 두 손가락의 거리 비가 배율, 가운데가 붙드는 점, 가운데의 움직임이 이동. */
export function pinch(
  camera: Camera,
  before: { a: { x: number; y: number }; b: { x: number; y: number } },
  after: { a: { x: number; y: number }; b: { x: number; y: number } },
): Camera {
  const dist = (pair: typeof before): number => Math.hypot(pair.a.x - pair.b.x, pair.a.y - pair.b.y);
  const mid = (pair: typeof before) => ({ x: (pair.a.x + pair.b.x) / 2, y: (pair.a.y + pair.b.y) / 2 });
  const from = mid(before);
  const to = mid(after);
  const zoomed = zoomAt(camera, dist(after) / Math.max(1, dist(before)), from);
  return panBy(zoomed, to.x - from.x, to.y - from.y);
}

/** 휠 한 칸의 배율 — 마우스 휠(큰 걸음)도 트랙패드(잔 걸음)도 같은 손맛으로. 핀치(ctrl)는 더 민감하게. */
export function wheelFactor(deltaY: number, deltaMode: number, pinching: boolean): number {
  const pixels = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY;
  const rate = pinching ? 0.01 : 0.0018;
  return Math.exp(-Math.max(-120, Math.min(120, pixels)) * rate);
}
